import { useState, useRef } from 'react';
import jsQR from 'jsqr';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL;

export default function Timbratura() {
  const { utente } = useAuth();
  const [fase, setFase] = useState('idle');
  const [risultato, setRisultato] = useState(null);
  const [errore, setErrore] = useState('');
  const inputRef = useRef(null);

  const elaboraImmagine = async (file) => {
    setFase('scanning');
    setErrore('');
    try {
      const bitmap = await createImageBitmap(file);
      const dimensioni = [800, 1200, 400, 1600, 2000];
      let code = null;

      for (const dim of dimensioni) {
        const canvas = document.createElement('canvas');
        const scala = Math.min(dim / bitmap.width, dim / bitmap.height);
        canvas.width = Math.round(bitmap.width * scala);
        canvas.height = Math.round(bitmap.height * scala);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        code = jsQR(imageData.data, imageData.width, imageData.height, {
          inversionAttempts: 'attemptBoth'
        });
        if (code) break;
      }

      if (!code) {
        setErrore('QR non trovato. Inquadra bene il QR e riprova.');
        setFase('idle');
        return;
      }

      let data;
      try {
        data = JSON.parse(code.data);
      } catch {
        setErrore('QR non valido');
        setFase('idle');
        return;
      }

      if (!data.reparto_id) {
        setErrore('QR non valido');
        setFase('idle');
        return;
      }

      // Recupera il token corrente dal backend
      const res = await axios.get(`${API}/token-reparto/${data.reparto_id}`);
      setRisultato({ token: res.data.token, reparto_id: data.reparto_id });
      setFase('confirm');

    } catch (e) {
      if (e.response?.status === 404) {
        setErrore('Reparto non trovato. Contatta l\'amministratore.');
      } else {
        setErrore('Errore nella lettura del QR. Riprova.');
      }
      setFase('idle');
    }
  };

  const conferma = async (tipo) => {
    try {
      const res = await axios.post(`${API}/timbratura`, {
        qr_token: risultato.token,
        tipo
      });
      setRisultato(prev => ({ ...prev, tipo, timestamp: res.data.timbratura.timestamp }));
      setFase('success');
      setTimeout(() => setFase('idle'), 4000);
    } catch (e) {
      setErrore(e.response?.data?.error || 'Errore durante la timbratura');
      setFase('idle');
    }
  };

  return (
    <div style={{ padding: '24px 16px', paddingBottom: 90 }}>
      <h2 style={{ color: '#f1f5f9', fontSize: 22, fontWeight: 800, marginBottom: 6 }}>Timbratura Smart</h2>
      <p style={{ color: '#64748b', fontSize: 14, marginBottom: 24 }}>Scansiona il QR code del tuo reparto</p>

      <input ref={inputRef} type="file" accept="image/*" capture="environment"
        style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0]) elaboraImmagine(e.target.files[0]); }} />

      {fase === 'idle' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 240, height: 240, margin: '0 auto 28px', borderRadius: 24, background: '#0f172a', border: '2px dashed #334155', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <div style={{ fontSize: 52 }}>📷</div>
            <p style={{ color: '#475569', fontSize: 13, margin: 0 }}>Premi per scansionare</p>
          </div>
          {errore && (
            <p style={{ color: '#ef4444', fontSize: 14, marginBottom: 16, background: '#ef444418', padding: '10px 16px', borderRadius: 10, border: '1px solid #ef444444' }}>
              {errore}
            </p>
          )}
          <button onClick={() => { inputRef.current.value = ''; inputRef.current.click(); }}
            style={{ background: 'linear-gradient(135deg, #f59e0b, #ef4444)', border: 'none', borderRadius: 16, padding: '16px 40px', color: '#0f172a', fontWeight: 800, fontSize: 16, cursor: 'pointer', boxShadow: '0 0 30px #f59e0b44' }}>
            📷 Scansiona QR Code
          </button>
          <p style={{ color: '#475569', fontSize: 12, marginTop: 16 }}>Il QR è affisso all'ingresso del tuo reparto</p>
        </div>
      )}

      {fase === 'scanning' && (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🔍</div>
          <p style={{ color: '#f59e0b', fontSize: 16, fontWeight: 600 }}>Analisi QR in corso...</p>
        </div>
      )}

      {fase === 'confirm' && risultato && (
        <div style={{ background: '#1e293b', borderRadius: 20, padding: 24, border: '1px solid #334155', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
          <h3 style={{ color: '#f1f5f9', fontSize: 20, fontWeight: 800, margin: '0 0 6px' }}>QR rilevato!</h3>
          <p style={{ color: '#64748b', fontSize: 14, marginBottom: 20 }}>Seleziona il tipo di timbratura</p>
          <div style={{ background: '#0f172a', borderRadius: 12, padding: 16, textAlign: 'left', marginBottom: 20 }}>
            {[
              ['Dipendente', `${utente?.nome} ${utente?.cognome}`],
              ['Orario', new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })]
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #1e293b' }}>
                <span style={{ color: '#475569', fontSize: 13 }}>{k}</span>
                <span style={{ color: '#f1f5f9', fontSize: 13, fontWeight: 600 }}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
            <button onClick={() => conferma('ingresso')}
              style={{ flex: 1, padding: 13, background: '#10b981', border: 'none', borderRadius: 12, color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: 14 }}>
              → Ingresso
            </button>
            <button onClick={() => conferma('uscita')}
              style={{ flex: 1, padding: 13, background: '#ef4444', border: 'none', borderRadius: 12, color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: 14 }}>
              ← Uscita
            </button>
          </div>
          <button onClick={() => setFase('idle')}
            style={{ width: '100%', padding: 11, background: '#0f172a', border: '1px solid #334155', borderRadius: 12, color: '#94a3b8', fontWeight: 700, cursor: 'pointer', fontSize: 14 }}>
            Annulla
          </button>
        </div>
      )}

      {fase === 'success' && (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: '#10b98118', border: '2px solid #10b981', margin: '0 auto 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>✓</div>
          <h3 style={{ color: '#10b981', fontSize: 22, fontWeight: 800, margin: '0 0 8px' }}>Timbratura Registrata!</h3>
          <p style={{ color: '#64748b', fontSize: 14 }}>La tua {risultato?.tipo} è stata salvata</p>
          <p style={{ color: '#f59e0b', fontSize: 18, fontWeight: 700 }}>{new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}</p>
        </div>
      )}
    </div>
  );
}
